import { DialogBody } from '@blueprintjs/core'
import { useQuery } from '@tanstack/react-query'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CSVLink } from 'react-csv'
import useBackendClient from '~/api/BackendClient/useBackendClient'
import type { BulkExportCultureMetadataResponse } from '~/api/backend/pages/bulkExportCultureMetadataAPI'
import type {
  CultureFilter,
  ObservationMetric,
} from '~/api/backend/pages/culturesPageAPI'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Button from '~/components/buttons/Button'
import Dialog from '~/components/Dialog'
import { Skeleton } from '~/components/ui/skeleton'
import { analytics } from '~/core/analytics'
import { useFeatureFlag } from '~/core/featureFlags'
import { CulturesFilterBar } from '~/pages/cultures/CulturesFilterBar/CultureFilterBar'
import cs from './export_picklist_dialog.scss'

export function ExportPicklistDialog(props: {
  open: boolean
  onClose: () => void
  initialFilters: CultureFilter[]
  selectedPlateBarcodes?: string[]
}) {
  const [filters, setFilters] = useState<CultureFilter[]>(props.initialFilters)

  useEffect(() => {
    setFilters(props.initialFilters)
  }, [props.initialFilters])

  const client = useBackendClient()

  const hackathon2025 = useFeatureFlag('hackathon2025') ?? false
  const requestedMetrics = useMemo<ObservationMetric[]>(
    () => (hackathon2025 ? ['cell_concentration'] : ['confluence']),
    [hackathon2025],
  )

  // Only fetch filtered cultures if no specific plates are selected
  const { data, isLoading: isLoadingCultures } = useQuery({
    queryKey: [QUERY_KEYS.CULTURE_PAGE_LIST, filters, requestedMetrics.join(',')],
    queryFn: () =>
      client.culturesPage.getFilteredCultures(
        {
          culture_filters: filters,
        },
        requestedMetrics,
      ),
    enabled: !props.selectedPlateBarcodes || props.selectedPlateBarcodes.length === 0,
  })

  const plateWithWells =
    data?.filteredCulturePlates?.reduce((acc, plate) => {
      acc[plate.barcode] = plate.wellCultures.map(culture => culture.well)
      return acc
    }, {}) ?? {}

  const { data: bulkExportPlates, isLoading: isLoadingExportData } = useQuery({
    queryKey: [
      QUERY_KEYS.CULTURE_PAGE_BULK_EXPORT,
      plateWithWells,
      props.selectedPlateBarcodes,
      filters,
    ],
    queryFn: () => {
      // If specific plates are selected, use those barcodes
      if (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0) {
        return client.bulkExportCultureMetadata.getData({
          plate_barcodes: props.selectedPlateBarcodes,
          culture_filters: filters,
        })
      }
      // Otherwise use the plate_with_wells from filtered cultures
      return client.bulkExportCultureMetadata.getData({
        plate_with_wells: plateWithWells,
        culture_filters: filters,
      })
    },
    enabled:
      Object.keys(plateWithWells).length > 0 ||
      (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0),
  })

  // Calculate plate and well counts
  const plateCount = useMemo(() => {
    if (props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0) {
      return props.selectedPlateBarcodes.length
    }
    return bulkExportPlates?.filteredCulturePlatesWithWells?.length ?? 0
  }, [props.selectedPlateBarcodes, bulkExportPlates])

  const wellCount = useMemo(() => {
    return (
      bulkExportPlates?.filteredCulturePlatesWithWells?.reduce(
        (acc, plate) => acc + plate.wellCultures.length,
        0,
      ) ?? 0
    )
  }, [bulkExportPlates])

  // Create picklist CSV data - this is where you'll customize the columns
  const csvData = useMemo(
    () => [
      [
        'SourceSite',
        'SourceWell',
        'SourceSiteBarcode',
        'SourceWellBarcode',
        'TargetSite',
        'TargetWell',
        'TargetSiteBarcode',
        'TargetWellBarcode',
        'Volume',
      ],
      ...(bulkExportPlates?.filteredCulturePlatesWithWells?.flatMap(plate => [
        ...plate.wellCultures.map(culture => [
          '', // Source site to be added by user
          culture.well,
          plate.barcode,
          '', // Source well barcode to be filled by user
          '', // TargetSite to be filled by user
          '', // TargetWell to be filled by user
          '', // TargetSiteBarcode to be filled by user
          '', // TargetWellBarcode to be filled by user
          '', //  Volume to be filled by user
        ]),
      ]) ?? []),
    ],
    [bulkExportPlates],
  )

  // Combine loading states based on which mode we're in
  const isLoading =
    props.selectedPlateBarcodes && props.selectedPlateBarcodes.length > 0
      ? isLoadingExportData // Only check export data loading when using selected plates
      : isLoadingCultures || isLoadingExportData // Check both when using filters

  // Check if there are any plates from either source
  const hasAnyPlates =
    (bulkExportPlates?.filteredCulturePlatesWithWells?.length ?? 0) > 0 ||
    Object.keys(plateWithWells).length > 0

  return (
    <Dialog
      className={cs.dialog}
      onClose={props.onClose}
      isOpen={props.open}
      noPadding={true}
    >
      <DialogBody className={cs.dialogBody}>
        <div className={cs.header}>
          <div className={cs.headerContent}>
            <div className={cs.titleSection}>
              <h2 className={cs.title}>Export Picklist</h2>
              <p className={cs.plateCount}>
                {plateCount} {plateCount === 1 ? 'PLATE' : 'PLATES'} SELECTED
              </p>
            </div>
            {isLoading ? (
              <Button
                type='primary'
                label='Export Hamilton Picklist (.csv)'
                disabled={true}
              />
            ) : (
              <CSVLink
                data={csvData}
                enclosingCharacter=''
                filename='hamilton-picklist.csv'
                className={cs.link}
              >
                <Button
                  type='primary'
                  label='Export Hamilton Picklist (.csv)'
                  onClick={() => {
                    analytics.track('Completed Export Picklist', {
                      numWells: wellCount,
                      numPlates: plateCount,
                    })
                  }}
                />
              </CSVLink>
            )}
          </div>
          <div className={cs.subHeader}>
            {isLoading ? (
              <div className={cs.skeleton}>
                <Skeleton className='h-4 w-48' />
              </div>
            ) : (
              <p className={cs.exportInfo}>
                Exporting {wellCount} wells from {plateCount}{' '}
                {plateCount === 1 ? 'plate' : 'plates'}
              </p>
            )}
          </div>
        </div>
        <div>
          <CulturesFilterBar
            className={cs.filterBar}
            filters={filters.filter(filter => filter.field === 'culture_status')}
            setFilters={setFilters}
          />
        </div>
        <div className={cs.body}>
          {isLoading ? (
            <LoadingSkeletons />
          ) : !hasAnyPlates ? (
            <div className={cs.noCulturesFound}>No cultures found</div>
          ) : (
            <PicklistTable
              plates={bulkExportPlates?.filteredCulturePlatesWithWells ?? []}
            />
          )}
        </div>
      </DialogBody>
    </Dialog>
  )
}

function LoadingSkeletons() {
  return (
    <div className={cs.tableContainer}>
      <table className={cs.table}>
        <thead className={cs.tableHeader}>
          <tr>
            <th className={cs.headerCell}>SOURCE SITE</th>
            <th className={cs.headerCell}>SOURCE WELL</th>
            <th className={cs.headerCell}>SOURCE SITE BARCODE</th>
            <th className={cs.headerCell}>SOURCE WELL BARCODE</th>
            <th className={cs.headerCell}>TARGET SITE</th>
            <th className={cs.headerCell}>TARGET WELL</th>
            <th className={cs.headerCell}>TARGET SITE BARCODE</th>
            <th className={cs.headerCell}>TARGET WELL BARCODE</th>
            <th className={cs.headerCell}>VOLUME</th>
          </tr>
        </thead>
        <tbody>
          {/* Generate skeleton rows */}
          {Array.from({ length: 10 }, (_, index) => (
            <tr key={index} className={cs.tableRow}>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-12' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-12' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-32' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-32' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-12' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-12' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-32' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-32' />
              </td>
              <td className={cs.tableCell}>
                <Skeleton className='h-4 w-16' />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PicklistTable({
  plates,
}: {
  plates: BulkExportCultureMetadataResponse['filteredCulturePlatesWithWells']
}) {
  // Ref for the scrolling container
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const mappedData = useMemo(
    () =>
      plates.flatMap(plate =>
        plate.wellCultures.map(culture => ({
          sourceSite: '',
          sourceWell: culture.well,
          sourceSiteBarcode: plate.barcode,
          sourceWellBarcode: '',
          targetSite: '',
          targetWell: '',
          targetSiteBarcode: '',
          targetWellBarcode: '',
          volume: '',
        })),
      ),
    [plates],
  )

  const columns = useMemo(
    () => [
      {
        header: 'SOURCE SITE',
        accessorKey: 'sourceSite',
      },
      {
        header: 'SOURCE WELL',
        accessorKey: 'sourceWell',
      },
      {
        header: 'SOURCE SITE BARCODE',
        accessorKey: 'sourceSiteBarcode',
      },
      {
        header: 'SOURCE WELL BARCODE',
        accessorKey: 'sourceWellBarcode',
      },
      {
        header: 'TARGET SITE',
        accessorKey: 'targetSite',
      },
      {
        header: 'TARGET WELL',
        accessorKey: 'targetWell',
      },
      {
        header: 'TARGET SITE BARCODE',
        accessorKey: 'targetSiteBarcode',
      },
      {
        header: 'TARGET WELL BARCODE',
        accessorKey: 'targetWellBarcode',
      },
      {
        header: 'VOLUME',
        accessorKey: 'volume',
      },
    ],
    [],
  )

  const table = useReactTable({
    data: mappedData ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const { rows } = table.getRowModel()

  // Set up the virtualizer
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 50, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside of visible area
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  // Calculate padding to maintain scroll position
  const paddingTop = virtualRows.length > 0 ? (virtualRows[0]?.start ?? 0) : 0
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0

  return (
    <div ref={tableContainerRef} className={cs.tableContainer}>
      <table className={cs.table}>
        <thead className={cs.tableHeader}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th className={cs.headerCell} key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr>
              <td colSpan={columns.length} style={{ height: paddingTop }} />
            </tr>
          )}
          {virtualRows.map(virtualRow => {
            const row = rows[virtualRow.index]
            return (
              <tr
                key={row.id}
                className={cs.tableRow}
                style={{ height: virtualRow.size }}
              >
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id} className={cs.tableCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            )
          })}
          {paddingBottom > 0 && (
            <tr>
              <td colSpan={columns.length} style={{ height: paddingBottom }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
